export const PROVIDER_PRESETS = {
  OPENAI: { label: "OpenAI", protocol: "OPENAI_RESPONSES", baseUrl: "https://api.openai.com/v1/responses", defaultModel: "gpt-4.1-mini" },
  GEMINI: { label: "Gemini", protocol: "GEMINI_GENERATE_CONTENT", baseUrl: "https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent", defaultModel: "gemini-2.0-flash" },
  ANTHROPIC: { label: "Claude", protocol: "ANTHROPIC_MESSAGES", baseUrl: "https://api.anthropic.com/v1/messages", defaultModel: "claude-3-5-haiku-latest" },
  DEEPSEEK: { label: "DeepSeek", protocol: "OPENAI_CHAT", baseUrl: "https://api.deepseek.com/v1/chat/completions", defaultModel: "deepseek-chat" },
  CUSTOM: { label: "CUSTOM", protocol: "OPENAI_RESPONSES", baseUrl: "", defaultModel: "" },
};

export const PROVIDER_CODE_OPTIONS = Object.entries(PROVIDER_PRESETS)
  .map(([value, preset]) => ({ value, label: preset.label }));

export const PROVIDER_PROTOCOL_OPTIONS = [
  "OPENAI_RESPONSES",
  "OPENAI_CHAT",
  "GEMINI_GENERATE_CONTENT",
  "ANTHROPIC_MESSAGES",
].map((value) => ({ value, label: value }));

export function credentialScopeOptions(isSuperAdmin) {
  return isSuperAdmin
    ? [{ value: "USER", label: "USER" }, { value: "SYSTEM", label: "SYSTEM" }]
    : [{ value: "USER", label: "USER" }];
}

export function providerPresetValues(providerCode) {
  const preset = PROVIDER_PRESETS[providerCode];
  if (!preset) return {};
  return {
    providerCode,
    protocol: preset.protocol,
    baseUrl: preset.baseUrl,
    defaultModel: preset.defaultModel,
    name: preset.label,
  };
}

export function geminiOperationEndpoint(model) {
  return `https://generativelanguage.googleapis.com/v1beta/models/${String(model || "").trim()}:generateContent`;
}

export function providerModelEndpointValues(providerCode, nextModel, previousModel, currentBaseUrl) {
  if (providerCode !== "GEMINI") return {};
  const generatedEndpoint = /^https:\/\/generativelanguage\.googleapis\.com\/v1beta\/models\/[^/:]+:generateContent$/;
  if (currentBaseUrl !== geminiOperationEndpoint(previousModel) && !generatedEndpoint.test(currentBaseUrl || "")) return {};
  return { baseUrl: geminiOperationEndpoint(nextModel) };
}

export function providerCreateValues(providerCode = "OPENAI") {
  return {
    providerType: "llm",
    enabled: true,
    capabilities: ["script"],
    credentialScope: "USER",
    defaultProvider: false,
    ...providerPresetValues(providerCode),
  };
}

export function providerEditValues(provider) {
  const {
    name,
    providerType,
    baseUrl,
    defaultModel,
    capabilities,
    notes,
    enabled,
    credentialScope,
    providerCode,
    protocol,
    defaultProvider,
  } = provider;
  return {
    name,
    providerType,
    baseUrl,
    defaultModel,
    capabilities,
    notes,
    enabled,
    credentialScope,
    providerCode,
    protocol,
    defaultProvider,
    apiKey: undefined,
  };
}

export function filterProvidersByScope(providers, isSuperAdmin, scopeFilter) {
  if (!isSuperAdmin || scopeFilter === "ALL") return providers;
  return providers.filter((provider) => provider.credentialScope === scopeFilter);
}

export function canApplyProviderLoad({
  mounted,
  requestGeneration,
  currentGeneration,
  requestedUserId,
  currentUserId,
}) {
  return Boolean(mounted
    && requestGeneration === currentGeneration
    && requestedUserId === currentUserId);
}

const PROVIDER_REQUEST_FIELDS = [
  "name",
  "providerType",
  "baseUrl",
  "defaultModel",
  "apiKey",
  "capabilities",
  "notes",
  "enabled",
  "providerCode",
  "protocol",
  "credentialScope",
  "defaultProvider",
];

export function providerRequestPayload(values, isSuperAdmin) {
  const payload = {};
  PROVIDER_REQUEST_FIELDS.forEach((field) => {
    if (values[field] !== undefined) payload[field] = values[field];
  });
  payload.credentialScope = isSuperAdmin && values.credentialScope === "SYSTEM" ? "SYSTEM" : "USER";
  if (typeof payload.apiKey === "string") {
    payload.apiKey = payload.apiKey.trim();
    if (!payload.apiKey) delete payload.apiKey;
  }
  return payload;
}

export function submitProviderForm(providerApi, editingId, values, isSuperAdmin) {
  const payload = providerRequestPayload(values, isSuperAdmin);
  return editingId ? providerApi.update(editingId, payload) : providerApi.create(payload);
}

export function canManageProvider(provider, profile) {
  if (!provider || !profile) return false;
  if (provider.credentialScope === "SYSTEM") return profile.role === "SUPER_ADMIN";
  return Boolean(profile.id) && String(provider.ownerId) === String(profile.id);
}
